import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { Post } from '@sprinklr/stories/post/Post';
import { getEndianTime } from 'utils/Number/NumberUtils';
import styled, { css } from 'components/_UI/designSystem';
import NativeProfileLockup from './Parts/nativeProfileLockup/NativeProfileLockup';
import { StyledTimeStamp, StyledLocation } from './Parts/nativeProfileLockup/styledComponents';
import NativeEngagement from './Parts/nativeEngagement/NativeEngagement';
import NativeText from './Parts/nativeText/NativeText';
import NativeMedia from './Parts/nativeMedia/NativeMedia';
import NativeTitle from './Parts/nativeTitle/NativeTitle';
import { PlayerInterval } from 'utils/PlayerInterval/PlayerInterval';
import {
    getNativeMediaOverrides,
    useNativePost,
    baseStyles,
    getEntityStyles,
    StyledMeta,
    getNativeColor,
} from './Helpers';
import NativeFlex from './Bits/NativeFlex';
import { NativeStylingOptions, NativeColorMode } from '../../widgets/PostsWidget/types';
import { usePostClickableEntities } from '@sprinklr/display-builder/hooks/usePostClickableEntities';

export type NativePostProps = {
    post: Post;
    playerInterval?: PlayerInterval;
    options?: NativeStylingOptions;
    extraPostDataTags?: React.ReactNode;
};
const Colors = {
    blue: '#2d8dc2',
    mediumGray: '#777',
};
const theme = {
    light: {
        link: Colors.blue,
        secondary: Colors.mediumGray,
    },
    dark: {
        link: Colors.blue,
        secondary: Colors.mediumGray,
    },
};
export type StyledPostProps = {
    colorMode: NativeColorMode;
};
const StyledPost = styled(NativeFlex)`
    ${(props: NativePostProps) => baseStyles(props.options)}
    ${({ ratio }: any) => getNativeMediaOverrides(ratio > 2.3 ? 40 : 60, 60)}
    ${({ colorMode }: StyledPostProps) => getEntityStyles(getNativeColor(theme, colorMode, 'link'))}
    ${({ colorMode }: StyledPostProps) =>
        css`
            .extra_post_data_value {
                width: auto;
                color: ${getNativeColor(theme, colorMode, 'secondary')};
            }
        `}
`;

const DefaultPost: React.FC<NativePostProps> = ({
    post,
    playerInterval,
    options,
    extraPostDataTags,
}) => {
    const {
        lineClamp,
        maxCharacters,
        colorMode,
        removeUrls,
        emoji,
        hideZero,
        showTitle,
        showExpandedEntityUrls,
        clickableUrls,
    } = options;

    const {
        postRef,
        screenName,
        rssSource,
        profileImgUrl,
        images,
        videos,
        name,
        snCreatedTime,
        snStats,
        unique,
        snType,
        expandedTextEntities,
        verified,
        hasMedia,
        text,
        rtl,
        language,
        orientation,
        ratio,
        location,
        title,
        publicationInfo,
        profilePermalink,
        permalink,
    } = useNativePost(post, showExpandedEntityUrls);

    const { handlePermalinkClick } = usePostClickableEntities({
        permalink,
        profilePermalink,
        clickableUrls,
    });

    const resolvedPrimarySecondary = useMemo(() => {
        if (publicationInfo?.name) {
            return {
                primary: publicationInfo?.name,
                secondary: name || screenName,
            };
        }
        return {
            primary: name,
            secondary: rssSource || screenName,
        };
    }, [name, publicationInfo?.name, rssSource, screenName]);

    const titleLineClamp = hasMedia ? options.title.lineClamp.hasMedia : options.title.lineClamp;

    const timeStampAndEngagement = useMemo(
        () => (
            <NativeFlex bottom vertical gap='s'>
                <NativeFlex middle gap='s' noGrow>
                    {snCreatedTime && (
                        <StyledTimeStamp colorMode={colorMode} onClick={handlePermalinkClick}>
                            {getEndianTime(snCreatedTime, 'D MMM YYYY, h:mm a')}
                        </StyledTimeStamp>
                    )}
                    <StyledLocation colorMode={colorMode} location={location} />
                </NativeFlex>
                <NativeEngagement snStats={snStats} snType={snType} hideZero={hideZero} />
            </NativeFlex>
        ),
        [colorMode, handlePermalinkClick, hideZero, location, snCreatedTime, snStats, snType]
    );

    if (!post) {
        return <div key={unique} />;
    }

    return (
        <StyledPost
            ratio={ratio}
            key={unique}
            ref={postRef}
            hasMedia={hasMedia}
            vertical={orientation === 'vertical'}
            orientation={orientation}
            options={options}
            colorMode={colorMode}
            gap
            pad>
            <>
                {orientation === 'vertical' && (
                    <>
                        {(profileImgUrl || name || screenName || snType) && (
                            <NativeProfileLockup
                                colorMode={colorMode}
                                verified={verified}
                                snType={snType}
                                profileImgUrl={profileImgUrl}
                                clickableUrls={clickableUrls}
                                profilePermalink={profilePermalink}
                                permalink={permalink}
                                secondaryType='name'
                                {...resolvedPrimarySecondary}
                            />
                        )}
                        {showTitle && (
                            <NativeTitle
                                lineClamp={titleLineClamp}
                                title={title}
                                colorMode={colorMode}
                                emoji={emoji}
                            />
                        )}
                        {hasMedia && (
                            <NativeMedia
                                snType={snType}
                                playerInterval={playerInterval}
                                images={images}
                                videos={videos}
                            />
                        )}
                        {text && (
                            <NativeText
                                removeUrls={removeUrls}
                                emoji={emoji}
                                colorMode={colorMode}
                                hasMedia={hasMedia}
                                lineClamp={lineClamp}
                                maxCharacters={maxCharacters}
                                channel={snType}
                                text={text}
                                textEntities={expandedTextEntities && expandedTextEntities.message}
                                clickableUrls={clickableUrls}
                            />
                        )}
                        <NativeFlex gap='s' bottom between>
                            {timeStampAndEngagement}
                            {extraPostDataTags}
                        </NativeFlex>
                    </>
                )}

                {orientation === 'horizontal' && (
                    <>
                        {hasMedia && (
                            <NativeMedia
                                snType={snType}
                                playerInterval={playerInterval}
                                images={images}
                                videos={videos}
                            />
                        )}

                        <StyledMeta vertical gap>
                            {(profileImgUrl || name || screenName || snType) && (
                                <NativeProfileLockup
                                    colorMode={colorMode}
                                    verified={verified}
                                    snType={snType}
                                    profileImgUrl={profileImgUrl}
                                    clickableUrls={clickableUrls}
                                    profilePermalink={profilePermalink}
                                    permalink={permalink}
                                    secondaryType='name'
                                    {...resolvedPrimarySecondary}
                                />
                            )}

                            {showTitle && (
                                <NativeTitle
                                    title={title}
                                    colorMode={colorMode}
                                    emoji={emoji}
                                    lineClamp={titleLineClamp}
                                />
                            )}

                            {text && (
                                <NativeText
                                    removeUrls={removeUrls}
                                    emoji={emoji}
                                    colorMode={colorMode}
                                    hasMedia={hasMedia}
                                    lineClamp={lineClamp}
                                    maxCharacters={maxCharacters}
                                    channel={snType}
                                    text={text}
                                    rtl={rtl}
                                    language={language}
                                    textEntities={
                                        expandedTextEntities && expandedTextEntities.message
                                    }
                                    clickableUrls={clickableUrls}
                                />
                            )}

                            <NativeFlex bottom gap='s' between>
                                {timeStampAndEngagement}
                                {extraPostDataTags}
                            </NativeFlex>
                        </StyledMeta>
                    </>
                )}
            </>
        </StyledPost>
    );
};

DefaultPost.displayName = 'DefaultPost';
export default observer(DefaultPost);
